import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useToggle from '@/data/hook/useToggle';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import { CourseModule } from '@/models/CourseModule';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import { CoursesModelsType } from '@/utils/trees/TreeCourses';
import { useTranslation } from 'react-i18next';
import CoursePedTree from '@/components/ped-content/CoursePedTree';
import CoursesTreeList from '@/components/shared/CoursesTreeList';
import TreeAddButton from '@/components/shared/TreeAddButton';
import TreeItem, { TreeItemProps } from '@/components/shared/TreeItem';

export default function CourseModuleTree({
  item: courseModule,
  parentId,
}: TreeItemProps<CourseModule>) {
  const type: CoursesModelsType = 'module';
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.tree',
  });
  const { navigate } = useCourseTreeNavigate();
  const key = mountKey({ type, id: courseModule.id });
  const { tree } = useTreeCourses();
  const { isOpen, toggle, open } = useToggle();
  const { coursePath: selectedCoursePath } = usePedWorkspace();

  const parent = tree?.current.nodes[parentId].data;

  const {
    results: coursePaths,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
  } = useInfiniteService({
    ...coursePathsQueryKeys.list({
      moduleId: [courseModule.id],
      uniqueCourse: true,
    })._ctx.infinity,
    enabled: isOpen,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  return (
    <TreeItem
      objKey={key}
      item={courseModule}
      parentId={parentId}
      type={type}
      isLoading={isInitialLoading}
      onToggle={toggle}
      isOpen={isOpen}
      open={open}
    >
      <CoursesTreeList
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isInitialLoading}
        onReachEnd={fetchNextPage}
        hasNextPage={hasNextPage}
      >
        {coursePaths.map(coursePath => (
          <CoursePedTree
            key={coursePath.id}
            item={
              selectedCoursePath &&
              selectedCoursePath.course.id === coursePath.course.id
                ? selectedCoursePath
                : coursePath
            }
            parentId={key}
          />
        ))}
        <TreeAddButton
          label={t('newPedContent')}
          onClick={() => {
            navigate(
              ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(
                parent?.realId ?? 0,
                courseModule.id,
                'new',
              ),
            );
          }}
        />
      </CoursesTreeList>
    </TreeItem>
  );
}
